import {Body, Button, FlexBox, IconButton, Image, MdToHtml, TextLink, Title} from "../../components";
import {AssetItem, AssetItemContainer, AssetList, ImageWrapper, StyledFlexBox} from "../multipleSelectedModal/MultipleSelectedModal.module";
import {getThumbnails} from "../../utils";
import {AssetFile, UnifiedSearchResultProps} from "../../interfaces";
import {setNotification, useAcceptUsageRightsMutation, useLazyGetAssetDownloadUrlQuery} from "../../services";
import {openFileDownload} from "../../utils/assetFiles";
import {useEffect, useState} from "react";
import {useDispatch} from "../../hooks/redux";
import FavoriteModal from "../favoriteModal/FavoriteModal";
import {ShareModal} from "../index";
import {Link} from "react-router-dom";
import paths from "../../routing/paths";
import TextButton from "../../components/buttons/TextButton/TextButton";

export interface MultipleSelectedTabProps {
  selectedItems: Array<UnifiedSearchResultProps>;
  removeAsset: Function;
  updateUsageRights: Function;
  searchType: string;
  setIsLoading: Function;
  clearBySearchType: Function;
}
const MultipleSelectedTab = ({
  selectedItems,
  removeAsset,
  updateUsageRights,
  searchType,
  setIsLoading,
  clearBySearchType,
}: MultipleSelectedTabProps) => {
  
  const dispatch = useDispatch();
  const handleRemoveAsset = (id: string) => {
    removeAsset(id);
    // selectedItems.length <= 1 && onClose();
  };
  
  const handleClearBySearchType = () => {
    clearBySearchType(searchType);
  }
  
  const [getAssetUrl, { isFetching: assetFetching, data: downloadUrl }] =
    useLazyGetAssetDownloadUrlQuery();
  
  const [isDownloading, _isDownloading] = useState<boolean>(false);
  
  const downloadWithFetch = async (fileUrl: string): Promise<void> => {
    try {
      const response = await fetch(fileUrl, {
        method: 'GET', // Explicitly set method to GET
        mode: 'cors',    // Add CORS mode (if needed, depends on your setup)
        cache: 'no-cache' // Prevent caching of the presigned URL
      });
      
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Extract filename from the content-disposition header, if available
      const filename = getFilenameFromContentDisposition(response) || getFilenameFromUrl(fileUrl) || 'download.zip';
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      
      // Revoke the Blob URL to free up resources
      URL.revokeObjectURL(url);
      
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
      throw error; // Re-throw to be caught by the calling function
    }
  };

// Helper function to extract filename from URL
  const getFilenameFromUrl = (url: string): string | null => {
    try {
      const decodedUrl = decodeURIComponent(url);
      const filenameMatch = decodedUrl.match(/filename\*?=['"]?(?:UTF-8['"]*)?([^;"'\/]*)/);
      if (filenameMatch && filenameMatch[1]) {
        return filenameMatch[1];
      }
      return decodeURIComponent(decodedUrl.substring(decodedUrl.lastIndexOf('/') + 1));
    } catch (e) {
      console.warn("Could not decode or extract filename from URL:", url);
      return null;
    }
  };
  
  const getFilenameFromContentDisposition = (response: Response): string | null => {
    const contentDisposition = response.headers.get('Content-Disposition');
    if (contentDisposition) {
      const filenameRegex = /filename\*?=['"]?(?:UTF-8['"]*)?([^;"'\/]*)/i;
      const filenameMatch = contentDisposition.match(filenameRegex);
      if (filenameMatch && filenameMatch[1]) {
        return decodeURIComponent(filenameMatch[1]);
      }
      const filenameRegexSimple = /filename="([^"]*)"/i;
      const filenameMatchSimple = contentDisposition.match(filenameRegexSimple);
      if (filenameMatchSimple && filenameMatchSimple[1]) {
        return decodeURIComponent(filenameMatchSimple[1]);
      }
    }
    return null;
  };
  
  const downloadFile = async (assetId: string) => {
    try {
      const response = await getAssetUrl(assetId).unwrap();
      await downloadWithFetch(response);
    } catch (error) {
      console.error("Error downloading asset: ", error, " Asset Id: ", assetId);
    }
  };
  
  const downloadAllFiles = async () => {
    for (const selectedItem of selectedItems) {
      if (selectedItem.assetId) {
        if (selectedItem.assetId !== selectedItems[0].assetId) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        await downloadFile(selectedItem.assetId);
      }
    }
  };
  
  const handleDownload = async () => {
    _isDownloading(true);
    await downloadAllFiles();
    _isDownloading(false);
  };
  
  const [favoritesOpen, _setFavoritesOpen] = useState(false),
    handleFavorites = () => {
      _setFavoritesOpen(true);
    },
    [shareOpen, _setShareOpen] = useState(false),
    handleShare = () => {
      _setShareOpen(true);
    };
  
  const [acceptRights, { isLoading }] = useAcceptUsageRightsMutation();
  const callUsageRights = (id: string) => {
      acceptRights(id)
      .unwrap()
      .then(() => {
        updateUsageRights(id);
        
        dispatch(
          setNotification({
            type: "success",
            message: "Usage rights accepted.",
          })
        );
      })
      .catch((error: any) => {
        dispatch(
          setNotification({
            type: "error",
            message: "Rights not accepted",
          })
        );
        console.error("error accepting usage rights: ", error);
      });
    };
  
  useEffect(() => {
    if (isLoading || assetFetching) {
      setIsLoading(true);
    } else if (!isLoading && !assetFetching) {
      setIsLoading(false);
    }
  }, [isLoading, assetFetching]);
  
  const getLinkUrl = (item: UnifiedSearchResultProps): string => {
    let itemUrl = item.url;
    if (item.searchType === 'template') {
      itemUrl = `/templates/details/${item.assetId}`;
    } else if (item.searchType === 'retail') {
      itemUrl = `/retail/branded-environments/design-advisories/details/${item.assetId}`;
    } else if (item.searchType === 'asset') {
      itemUrl = `/assets/details/${item.assetId}`;
    } else if (item.searchType === 'training') {
      itemUrl = `${paths.TRAINING_VIDEOS}/${item.playlistSlug}/${item.videoSlug}`;
    }
    return itemUrl;
  };
  
  const getSearchType = (): string => {
    let tabSearchType = "Asset";
    if (searchType === "guideline") {
      tabSearchType = "Guideline";
    } else if (searchType === "compliance") {
      tabSearchType = "Compliance";
    } else if (searchType === "training") {
      tabSearchType = "Training";
    } else if (searchType === "template") {
      tabSearchType = "Template";
    } else if (searchType === "retail") {
      tabSearchType = "Retail";
    }
    return tabSearchType;
  };
  
  return (
    <>
      <StyledFlexBox data-testid="multiple-selected-tab">
        <FlexBox row align="center" justify="space-between" pd="0 0 2rem">
          <Title size="large" bold pd="1rem 0">
            {`${getSearchType()} selection${selectedItems.length > 1 ? "s" : ""}`}
          </Title>
          {selectedItems.length > 1 && (
            <Button data-testid="clear-by-search-type" size="small" use="secondary" onClick={handleClearBySearchType}>
              Remove all {getSearchType()}{(searchType === "template" || searchType === "asset" || searchType === "guideline") ? "s" : ""}
            </Button>
          )}
        </FlexBox>
        <AssetList>
          {selectedItems.map((selectedItem: UnifiedSearchResultProps) => (
            <AssetItemContainer>
            <AssetItem key={selectedItem.id}>
              {selectedItem.files && selectedItem.files.length > 0 && (
                <ImageWrapper>
                  <Link
                    to={getLinkUrl(selectedItem)}
                    // onClick={onAssetLinkClick}
                    className="media__link"
                    title={`Open new tab to view image: ${selectedItem.title}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="asset-details-link"
                  >
                    <Image src={getThumbnails(selectedItem.files, "medium")[0]} />
                  </Link>
                </ImageWrapper>
              )}
              <FlexBox pd="1rem 0">
                <Body size="large" bold>{selectedItem.category}</Body>
                <TextLink
                  inline
                  newTab
                  key={selectedItem.id}
                  size="large"
                  to={getLinkUrl(selectedItem)}
                >
                  {selectedItem.title}
                </TextLink>
                {selectedItem.files && selectedItem.files.length > 0 && (
                  <Body pd="0.5rem 0 0">{`${selectedItem.files.length} file${
                    selectedItem.files.length !== 1 ? "s" : ""
                  }`}</Body>
                )}
                {((searchType === "asset" || searchType === "template" || searchType === "retail") && selectedItem.userActions && !selectedItem.userActions.hasAcceptedUsageRights) && (
                  <FlexBox data-testid="accept-rights">
                    <FlexBox pd="0 0 1rem">
                      <Body pd="0.25rem 0" color="#0077b4">
                        You must accept the usage rights before downloading
                        this asset.
                      </Body>
                      <Body maxWidth="80ch">
                        Permission to download an image has been given on the
                        condition that the image is only used in accordance
                        with the usage rights listed, including any term
                        period limitations. Any other use, which has not been
                        explicitly authorized, is prohibited. Any breach of
                        the usage rights may result in copyright infringement
                        or other liability for Verizon, for which you will
                        bear full legal responsibility.
                      </Body>
                      {/* <StyledTooltip size="small" data-tooltip="test">
                                    Permission to download an image has been given on the
                                    condition that the image is only used in accordance
                                    with the usage rights listed, including any term
                                    period limitations. Any other use, which has not been
                                    explicitly authorized, is prohibited. Any breach of
                                    the usage rights may result in copyright infringement
                                    or other liability for Verizon, for which you will
                                    bear full legal responsibility.
                                  </StyledTooltip> */}
                    </FlexBox>
                    {selectedItem.usageRightsCopy && (
                      <MdToHtml
                        pSize="large"
                        pMargin="0 0 0.5rem 0"
                        content={`**Usage rights:** ${selectedItem.usageRightsCopy}`}
                      />
                    )}
                    <Button
                      use="primary"
                      onClick={() => callUsageRights(selectedItem.assetId ? selectedItem.assetId : "")}
                      disabled={assetFetching || isLoading}
                      data-testid="update-rights-button"
                    >
                      Accept
                    </Button>
                  </FlexBox>
                )}
              </FlexBox>
            </AssetItem>
              <TextButton
                onClick={() => handleRemoveAsset(selectedItem.id)}
                disabled={assetFetching}
                testId="remove-asset"
              >Remove
              </TextButton>
            </AssetItemContainer>
          ))}
        </AssetList>
        <FlexBox row pd="2rem 0 0" align="center" gap="1rem">
          {(searchType === "asset" || searchType === "template" || searchType === "retail") && (
            <Button
              use="primary"
              onClick={handleDownload}
              disabled={assetFetching || isDownloading || isLoading || selectedItems.some((item) => item.userActions && !item.userActions.hasAcceptedUsageRights && item.usageRightsCopy)}
              data-testid="download-button"
            >
              Download
            </Button>
          )}
          
          <IconButton
            size="large"
            name="add-to-favorite"
            onClick={handleFavorites}
            noHover
            disabled={assetFetching || isDownloading || isLoading}
            testId="open-favorites"
          />
          <IconButton
            size="large"
            name="share"
            onClick={handleShare}
            noHover
            disabled={assetFetching || isDownloading || isLoading}
            testId="open-share"
          />
        </FlexBox>
      </StyledFlexBox>
      {favoritesOpen && (
        <FavoriteModal
          items={selectedItems}
          onClose={() => _setFavoritesOpen(false)}
          // onRemoveClick={(id: string) => removeAsset(id)}
          hideBackdrop
        />
      )}
      {shareOpen && (
        <ShareModal
          items={selectedItems}
          onClose={() => _setShareOpen(false)}
          // onRemoveClick={(id: string) => removeAsset(id)}
          hideBackdrop
        />
      )}
    </>
  );
};

export default MultipleSelectedTab;